import { useQuery } from "@tanstack/react-query";
import { Temperamento } from "./mascotasServicios";
import { get, patch, post } from "./divertidogsApiUtils";
import { getFileExtension } from "../utils/fileUtils";
import {
  objectToFormData,
  objectToFormDataWithNulls,
} from "../utils/formDataUtils";
import { hoy } from "../utils/dateUtils";

export type EstadoReporteDiario =
  | "SIN_REPORTE"
  | "REPORTE_CREADO"
  | "REPORTE_EN_ENVIO"
  | "REPORTE_ENVIADO"
  | "ENVIO_FALLIDO";

export interface MascotaReporteDiario {
  idAsistencia: string;
  idMascota: string;
  nombreMascota: string;
  nombrePropietario: string;
  estado: EstadoReporteDiario;
}

const listarMascotasReporteDiario = (): Promise<
  MascotaReporteDiario[] | undefined
> => {
  return get<MascotaReporteDiario[]>("reportes_diarios/mascotas?fecha=" + hoy());
};

export const QUERY_KEY_OBTENER_MASCOTAS_REPORTE_DIARIO =
  "QUERY_KEY_OBTENER_MASCOTAS_REPORTE_DIARIO";

export const useObtenerMascotasReporteDiario = () => {
  return useQuery(
    [QUERY_KEY_OBTENER_MASCOTAS_REPORTE_DIARIO],
    async () => await listarMascotasReporteDiario()
  );
};

export type EstadoAnimo = "Triste" | "Alegre" | "Tranquilo" | "Ansioso";
export const ESTADOS_DE_ANIMO: EstadoAnimo[] = [
  "Triste",
  "Alegre",
  "Tranquilo",
  "Ansioso",
];

export interface ReporteDiario {
  id: string;
  idAsistencia: string;
  desayuno: boolean | null;
  almorzo: boolean | null;
  ceno: boolean | null;
  observacionesAlimenticias: string | null;
  observacionesMedicas: string | null;
  tomoMedicamentos: boolean | null;
  temperamento: Temperamento;
  observacionesSocializacion: string | null;
  estadoAnimo: EstadoAnimo;
  novedades: string | null;
  fotoNovedades: string | null;
  fotoGeneral: string | null;
  videoNovedades: string | null;
}

const buscarReporteDiario = (
  idAsistencia: string
): Promise<ReporteDiario | undefined> => {
  return get<ReporteDiario>(`reportes_diarios?idAsistencia=${idAsistencia}`);
};

export const QUERY_KEY_BUSCAR_REPORTE_DIARIO =
  "QUERY_KEY_BUSCAR_REPORTE_DIARIO";

export const useBuscarReporteDiario = (idAsistencia: string) => {
  return useQuery(
    [QUERY_KEY_BUSCAR_REPORTE_DIARIO],
    async () => await buscarReporteDiario(idAsistencia)
  );
};

export type NuevoReporteDiario = Omit<
  ReporteDiario,
  "id" | "fotoGeneral" | "fotoNovedades" | "videoNovedades"
> & {
  fotoGeneral: File | null;
  fotoNovedades: File | null;
  videoNovedades: File | null;
};

export const crearReporteDiario = (nuevoReporteDiario: NuevoReporteDiario) => {
  const formData = objectToFormData(nuevoReporteDiario);
  return post<FormData>("reportes_diarios", formData);
};

export const enviarReporteDiario = (idAsistencia: string) => {
  return post(`reportes_diarios/enviar?idAsistencia=${idAsistencia}`);
};

export interface EdicionesReporteDiario {
  id: string;
  desayuno?: boolean | null;
  almorzo?: boolean | null;
  ceno?: boolean | null;
  observacionesAlimenticias?: string | null;
  observacionesMedicas?: string | null;
  tomoMedicamentos?: boolean | null;
  temperamento?: Temperamento;
  observacionesSocializacion?: string | null;
  estadoAnimo?: EstadoAnimo;
  novedades?: string | null;
  fotoNovedades?: File | null;
  eliminarFotoNovedades?: Boolean;
  fotoGeneral?: File | null;
  eliminarFotoGeneral?: Boolean;
  videoNovedades?: File | null;
  eliminarVideoNovedades?: Boolean;
}

export type EdicionesReporteDiarioRequest = Omit<EdicionesReporteDiario, "id">;

export const modificarReporteDiario = (
  edicionesReporteDiario: EdicionesReporteDiario
) => {
  const formData = objectToFormDataWithNulls(edicionesReporteDiario);
  return patch<FormData>(
    "reportes_diarios/" + edicionesReporteDiario.id,
    formData
  );
};

export function esFotoValida(fileName: string): boolean {
  var ext = getFileExtension(fileName);
  switch (ext.toLowerCase()) {
    case "jpeg":
    case "jpg":
    case "png":
      return true;
  }
  return false;
}

export function esVideoValido(fileName: string): boolean {
  var ext = getFileExtension(fileName);
  switch (ext.toLowerCase()) {
    case "3gp":
    case "mp4":
      return true;
  }
  return false;
}

export const reporteFueCreado = (reporte: MascotaReporteDiario): boolean => {
  return (
    reporte.estado === "REPORTE_CREADO" ||
    reporte.estado === "REPORTE_ENVIADO" ||
    reporte.estado === "REPORTE_EN_ENVIO" ||
    reporte.estado === "ENVIO_FALLIDO"
  );
};
